import config from "config";
import {
  gql,
  QueryFunctionOptions,
  useLazyQuery,
  useQuery,
} from "@apollo/client";
import { ENTITY_STATUSES } from "const";
import { ENTITY_TYPE, IEntity, IOrganizationEntity } from "types";
import { useCheckIsAdmin, useCheckPremium, useCheckSuper } from "utils";
import { ORGANIZATION_FIELDS } from "./Organization.constants";
import { useRef, useState } from "react";

const GET_ORGANIZATIONS = gql`
  query getOrganizations(
    $where: EntityWhere
    $fundingRoundsWhere: FundingRoundWhere
    $sort: [EntitySort!]
    $limit: Int
    $offset: Int
    $phrase: String!
    $isAdmin: Boolean!
    $isAuth: Boolean!
  ) {
    organizationsAggregate: entitiesAggregate (where: $where, fulltext: { FullTextSearchFields: { phrase: $phrase } }) {
			count
		}
    organizations: entities (
      options: {
        limit: $limit,
        offset: $offset,
        sort: $sort
      },
      where: $where
      fulltext: { FullTextSearchFields: { phrase: $phrase } }
      ) {
      ${ORGANIZATION_FIELDS}
    }
  }
`;

type GraphCompanyVarsType = {
  limit?: number;
  offset?: number;
  sort?: {};
  where?: { [x: string]: any };
  search?: string | null;
  fulltext?: string;
};

export function useGraphQLCompanies({
  limit,
  offset,
  sort,
  where,
  search,
}: GraphCompanyVarsType) {
  const isAdmin = useCheckIsAdmin();
  const isSuper = useCheckSuper();
  const isPremium = useCheckPremium();
  const { loading, error, data } = useQuery<{
    organizations: IOrganizationEntity[];
    organizationsAggregate: { count: number };
  }>(GET_ORGANIZATIONS, {
    variables: {
      limit: isPremium ? limit : 30,
      where: {
        ...where,
        verticalProfiles_SOME: {
          ...where?.verticalProfiles_SOME,
          vertical: config.VERTICAL,
          entityTypeProfiles_SOME: {
            ...where?.verticalProfiles_SOME?.entityTypeProfiles_SOME,
            entityType: ENTITY_TYPE.ORGANIZATION,
          },
        },
      },
      sort: sort,
      offset: offset,
      phrase: search
        ? "+" + search.replaceAll("-", " ").replaceAll(" ", " +") + "*"
        : "*",
      isAdmin: isAdmin,
      isAuth: isPremium,
      fundingRoundsWhere: {
        ...(!isSuper &&
          !isAdmin && {
            status_IN: ENTITY_STATUSES.APPROVED,
          }),
      },
    },
  });

  return { loading, error, data };
}

export function useOrganizationExportQuery({
  sort,
  where,
  search,
}: GraphCompanyVarsType) {
  const isAdmin = useCheckIsAdmin();
  const isSuper = useCheckSuper();
  const isPremium = useCheckPremium();

  const exportDataRef = useRef<IEntity[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const [getCount] = useLazyQuery(GET_ORGANIZATIONS_COUNT);

  const [getOrganizations] = useLazyQuery<{ organizations: IEntity[] }>(
    GET_ORGANIZATIONS_EXPORT
  );

  const getExportData = async () => {
    setIsFetching(true);

    try {
      const { data: countResult } = await getCount({
        variables: {
          where: {
            ...where,
            verticalProfiles_SOME: {
              ...where?.verticalProfiles_SOME,
              vertical: config.VERTICAL,
              entityTypeProfiles_SOME: {
                ...where?.verticalProfiles_SOME?.entityTypeProfiles_SOME,
                entityType: ENTITY_TYPE.ORGANIZATION,
              },
            },
          },
          phrase: search
            ? "+" + search.replaceAll("-", " ").replaceAll(" ", " +") + "*"
            : "*",
          isAdmin: isAdmin,
          isAuth: isPremium,
        },
      });

      const isPrivilegedUser = isAdmin || isSuper;

      const totalOrganizations = isPrivilegedUser
        ? countResult?.organizationsAggregate?.count
        : Math.min(200, countResult?.organizationsAggregate?.count || 0);

      if (totalOrganizations === 0) {
        setIsFetching(false);
        return;
      }

      const LIMIT = !isPrivilegedUser ? totalOrganizations : 500;
      const totalRequests = Math.ceil(totalOrganizations / LIMIT);

      let accumulatedData: IEntity[] = [];

      for (let i = 0; i < totalRequests; i++) {
        const { data } = await getOrganizations({
          variables: {
            limit: LIMIT,
            offset: i * LIMIT,
            where: {
              ...where,
              verticalProfiles_SOME: {
                ...where?.verticalProfiles_SOME,
                vertical: config.VERTICAL,
                entityTypeProfiles_SOME: {
                  ...where?.verticalProfiles_SOME?.entityTypeProfiles_SOME,
                  entityType: ENTITY_TYPE.ORGANIZATION,
                },
              },
            },
            sort: sort,
            phrase: search
              ? "+" + search.replaceAll("-", " ").replaceAll(" ", " +") + "*"
              : "*",
            isAdmin: isAdmin,
            isAuth: isPremium,
            fundingRoundsWhere: {
              ...(!isSuper &&
                !isAdmin && {
                  status_IN: ENTITY_STATUSES.APPROVED,
                }),
            },
          },
        });

        if (data?.organizations) {
          accumulatedData = [...accumulatedData, ...data.organizations];
        }
      }

      exportDataRef.current = accumulatedData;
    } catch (err) {
      console.error(err);
      setError(err as Error);
    } finally {
      setIsFetching(false);
    }
  };

  return {
    loadingExport: isFetching,
    dataExport: exportDataRef.current,
    error,
    getExportData,
  };
}

const GET_ORGANIZATIONS_COUNT = gql`
  query getOrganizationsCount($where: EntityWhere, $phrase: String!) {
    organizationsAggregate: entitiesAggregate(
      where: $where
      fulltext: { FullTextSearchFields: { phrase: $phrase } }
    ) {
      count
    }
  }
`;

const GET_ORGANIZATIONS_EXPORT = gql`
  query getOrganizationsExport(
    $where: EntityWhere
    $fundingRoundsWhere: FundingRoundWhere
    $sort: [EntitySort!]
    $limit: Int
    $offset: Int
    $phrase: String!
    $isAdmin: Boolean!
    $isAuth: Boolean!
  ) {
    organizations: entities (
      options: {
        limit: $limit,
        offset: $offset,
        sort: $sort
      },
      where: $where
      fulltext: { FullTextSearchFields: { phrase: $phrase } }
      ) {
      ${ORGANIZATION_FIELDS}
    }
  }
`;

export function useEntitiesUserContextOptionsQuery({
  name,
  ...rest
}: {
  name: string | undefined;
} & QueryFunctionOptions<
  {
    entities: Partial<IEntity>[];
  },
  {
    phrase: string | undefined;
    where: {
      entityType_INCLUDES?: string;
      vertical_INCLUDES: string | undefined;
    };
  }
>) {
  const { loading, error, data } = useQuery<
    {
      entities: Partial<IEntity>[];
    },
    {
      phrase: string | undefined;
      where: {
        entityType_INCLUDES?: string;
        vertical_INCLUDES: string | undefined;
      };
    }
  >(
    gql`
      query getEntityOptions($where: EntityWhere, $phrase: String!) {
        entities: entitiesFulltextFullTextSearchFields(
          phrase: $phrase
          where: { score: { min: 0.5 }, entity: $where }
          sort: { score: DESC }
          limit: 10
        ) {
          score
          entity {
            id
            name
            logo
            vertical
            entityType
            employees {
              id
              email
              firstName
              lastName
              createdAt
              profilePic
              adminOf {
                id
              }
            }
            hasSubscription {
              id
              inTrial
              customPrice
              startsAt
              expiresAt
              members {
                id
                firstName
                lastName
                profilePic
                createdAt
                email
                jobTitle
                aboutMe
              }
              admins {
                id
                firstName
                lastName
                profilePic
                createdAt
                email
                jobTitle
                aboutMe
              }
              type {
                id
                name
                price
                maxSeats
                minSubscriptionMonths
                createdAt
              }
            }
          }
        }
      }
    `,
    {
      context: { api: "userProfile" },
      variables: {
        phrase: name?.replace("-", " ") + "*" || "",
        where: {
          vertical_INCLUDES: config.VERTICAL,
        },
      },
      ...rest,
    }
  );
  return { loading, error, data };
}

export type ContactDetails = {
  belongsToDepartments: string[];
  city: string;
  country: string;
  firstName: string;
  fullName: string;
  headline: string;
  id: string;
  lastName: string;
  linkedIdUrl: string;
  phoneNumbers: {
    sanitized_number: string;
    type: string;
  };
  photoUrl: string;
  seniority: string;
  state: string;
  title: string;
  twitterUrl: string;
};

const GET_CONTACT_DETAILS = gql`
  query ContactDetails(
    $domain: String!
    $inputOptions: ContactDetailsInputOptions
  ) {
    contactDetailsDepartments(domain: $domain)
    contactDetailsLocations(domain: $domain) {
      cities
      countries
      states
    }
    contactDetails(domain: $domain, inputOptions: $inputOptions) {
      page {
        totalElements
      }
      results {
        belongsToDepartments
        city
        country
        firstName
        fullName
        headline
        id
        lastName
        linkedIdUrl
        phoneNumbers {
          sanitized_number
          type
        }
        photoUrl
        seniority
        state
        title
        twitterUrl
      }
    }
  }
`;

type ContactDetailsVars = {
  domain: string;
  limit?: number;
  offset?: number;
  departmentFilter?: string | null | undefined;
  cityFilter?: string | null | undefined;
  stateFilter?: string | null | undefined;
  countryFilter?: string | null | undefined;
  sort?: {};
};

type ContactDetailsResult = {
  contactDetailsDepartments: string[];
  contactDetailsLocations: {
    cities: string[];
    countries: string[];
    states: string[];
  };
  contactDetails: {
    page: {
      totalElements: number;
    };
    results: ContactDetails[];
  };
};

export function useContactDetails({
  domain,
  limit,
  offset,
  departmentFilter,
  cityFilter,
  stateFilter,
  countryFilter,
  sort,
}: ContactDetailsVars) {
  const { loading, error, data } = useQuery<ContactDetailsResult>(
    GET_CONTACT_DETAILS,
    {
      variables: {
        domain: domain || "",
        inputOptions: {
          limit: limit || 30,
          offset: offset || 0,
          departmentFilter: departmentFilter || "",
          cityFilter: cityFilter || "",
          stateFilter: stateFilter || "",
          countryFilter: countryFilter || "",
          ...(sort && {
            sort,
          }),
        },
      },
    }
  );

  return { loading, error, data };
}

export function useContactDetailsExportQuery({
  domain,
  limit,
  offset,
  departmentFilter,
  sort,
}: ContactDetailsVars) {
  const [getExportData, { loading: loadingExport, data: dataExport }] =
    useLazyQuery<ContactDetailsResult>(GET_CONTACT_DETAILS, {
      variables: {
        domain: domain || "",
        inputOptions: {
          limit: limit || 30,
          offset: offset || 0,
          departmentFilter: departmentFilter || "",
          ...(sort && {
            sort,
          }),
        },
      },
    });

  return { loadingExport, dataExport, getExportData };
}
